// Píxel de Meta para el sitio público (LDT-692, épica LDT-691).
//
// Es la ÚNICA puerta del navegador hacia Meta: el resto del código dispara
// eventos con `trackMeta()` y nunca llama a `window.fbq` directamente. Reglas,
// todas con test en src/test/meta-pixel-consentimiento.test.tsx:
//
//   - Sin consentimiento de MARKETING no sale nada. Se comprueba en CADA
//     llamada contra `readConsent()` (igual que `trackEvent()` con analítica),
//     no sólo al montar: una revocación en otra pestaña o un consentimiento
//     de otra versión cortan el envío al instante.
//   - Sólo en el sitio público, con una lista de rutas PERMITIDAS (no de
//     prohibidas): una ruta nueva queda fuera hasta que alguien la añade a
//     propósito. /platform, /app y /calendar tratan datos de familias y
//     menores, y la política de privacidad promete que no se usan para
//     marketing.
//   - Inerte sin `VITE_META_PIXEL_ID`: en local y en los previews sin la
//     variable no se carga nada. El ID es público (va en el HTML de cualquier
//     web con Píxel); por eso va en una variable VITE_ y no en un secret
//     (la versión revertida de junio lo pedía a una edge function, 68ac5d69).
//   - Nunca valores de campos en los parámetros: quien llama manda nombres de
//     paso, claves de pack o de módulo. La PII sólo viaja por la API de
//     conversiones, hasheada, desde el servidor (LDT-693, LDT-695).
//   - Revocar corta de verdad (`fbq('consent','revoke')`) y borra `_fbp`/`_fbc`:
//     no repetir LDT-460, donde Clarity sigue grabando tras retirar el
//     consentimiento.
//
// Base: la integración revertida el 2026-06-04 (`edcfc7dd`, revert `2c48b50a`),
// sin su función pública `meta-capi` (decisión de LDT-693).
import { readConsent } from "@/lib/cookie-consent";
import { MARKETING_PAGES } from "@/components/landing/flags";
import { stripLocale } from "@/lib/locale";

interface MetaFbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
  disablePushState?: boolean;
  allowDuplicatePageViews?: boolean;
}

declare global {
  interface Window {
    fbq?: MetaFbq;
    _fbq?: unknown;
  }
}

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

// Eventos estándar de Meta que usa la épica (se mandan con `track`). Los
// personalizados van con `trackCustom`: Meta los trata distinto y un evento
// estándar mandado como personalizado no cuenta para optimizar campañas.
const STANDARD_EVENTS = [
  "PageView",
  "ViewContent",
  "Contact",
  "Lead",
  "InitiateCheckout",
  "AddPaymentInfo",
  "StartTrial",
  "Subscribe",
] as const;
type MetaStandardEvent = (typeof STANDARD_EVENTS)[number];
// Embudo del stepper del checkout (LDT-694).
type MetaCustomEvent = "CheckoutStep" | "CheckoutError" | "CheckoutAbandoned";
export type MetaEventName = MetaStandardEvent | MetaCustomEvent;
export type MetaParams = Record<string, string | number | boolean | string[]>;

const STANDARD_SET: ReadonlySet<string> = new Set(STANDARD_EVENTS);

/** Pixel ID público. Se lee en cada uso (no al importar) para que los tests puedan cambiarlo. */
function getPixelId(): string {
  return ((import.meta.env.VITE_META_PIXEL_ID as string | undefined) ?? "").trim();
}

// Páginas del sitio público, sin prefijo de idioma. Mismo conjunto que
// `publicRoutes()` de App.tsx (el test de frontera compara las dos listas y la
// del prerender). /pricing, /features y /chain siguen su flag de publicación:
// con el flag apagado la ruta redirige a la home y no es una página.
const LOCALIZED_PUBLIC_PAGES: ReadonlySet<string> = new Set([
  "/",
  "/contact",
  "/about-us",
  "/privacy",
  "/terms",
  "/cookies",
  "/legal-notice",
  "/refunds",
  "/data-deletion",
  "/legal",
  "/dpa",
  "/subprocessors",
  "/security",
  // LDT-711: /health-consent NO está, a propósito. Es el texto del
  // consentimiento para datos de salud: que Meta sepa que alguien lo lee ya
  // insinúa algo sobre la salud de un niño.
  ...(Object.keys(MARKETING_PAGES) as (keyof typeof MARKETING_PAGES)[])
    .filter((page) => MARKETING_PAGES[page])
    .map((page) => `/${page}`),
]);

// Rutas públicas sin prefijo de idioma. /subscribe/success es la vuelta de la
// pasarela tras el alta: ahí el navegador manda el gemelo de la conversión que
// el servidor manda desde billing-webhook (LDT-695).
const NON_LOCALIZED_PUBLIC_PAGES: ReadonlySet<string> = new Set(["/subscribe/success"]);

// LDT-696: entrar en precios o en funcionalidades es además un `ViewContent`,
// una vez por ruta como el PageView (sin prefijo de idioma).
const VIEW_CONTENT_PAGES: Readonly<Record<string, string>> = {
  "/pricing": "pricing",
  "/features": "features",
};

// React Router casa sin distinguir mayúsculas ni la barra final.
function normalizePath(pathname: string): string {
  let path = pathname.toLowerCase();
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

/** ¿Es `pathname` una página del sitio público donde puede existir el Píxel? */
export function isMetaAllowedPath(pathname: string): boolean {
  if (!pathname || !pathname.startsWith("/")) return false;
  const path = normalizePath(pathname);
  if (NON_LOCALIZED_PUBLIC_PAGES.has(path)) return true;
  return LOCALIZED_PUBLIC_PAGES.has(stripLocale(path));
}

function marketingConsented(): boolean {
  return !!readConsent()?.marketing;
}

function currentPathname(): string {
  return typeof window === "undefined" ? "" : window.location.pathname;
}

function canSend(pathname: string): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (!getPixelId()) return false;
  if (!marketingConsented()) return false;
  return isMetaAllowedPath(pathname);
}

// Estado del módulo (una página = una vida del Píxel).
let initializedPixelId: string | null = null;
// true mientras el Píxel está en `consent revoke`: tras retirar el
// consentimiento o al navegar dentro de la SPA fuera del sitio público.
let paused = false;
let lastPageViewPath: string | null = null;

/** Inyecta el snippet oficial una sola vez e inicializa el Píxel. false = no hay ID. */
function ensurePixelLoaded(): boolean {
  const pixelId = getPixelId();
  if (!pixelId) return false;

  if (!window.fbq) {
    // Snippet oficial de Meta: la cola se llena hasta que fbevents.js carga y
    // la drena. Se inyecta desde un efecto, nunca en el render: el HTML
    // prerenderizado del sitio público no puede llevarlo incrustado.
    const fbq: MetaFbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue!.push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    // Sin esto fbevents.js manda un PageView por su cuenta en cada
    // history.pushState: duplicaría el de MetaPixelLoader y, al navegar dentro
    // de la SPA a /platform, mandaría eventos fuera del sitio público.
    fbq.disablePushState = true;
    // fbevents.js descarta un segundo PageView en la misma carga de página
    // salvo con esta bandera (QA de LDT-692 con el script real: en la SPA solo
    // llegaba el de la primera ruta). Los duplicados de verdad ya los evita
    // `trackMetaPageView`, que cuenta cada pathname una vez.
    fbq.allowDuplicatePageViews = true;
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = FBEVENTS_SRC;
    const first = document.getElementsByTagName("script")[0];
    if (first?.parentNode) first.parentNode.insertBefore(script, first);
    else (document.head || document.body).appendChild(script);
  }

  if (initializedPixelId !== pixelId) {
    // Sin configuración automática: nada de clics en botones ni metadatos de
    // la página que no hayamos decidido mandar. Tiene que ir ANTES de init.
    window.fbq("set", "autoConfig", false, pixelId);
    window.fbq("init", pixelId);
    initializedPixelId = pixelId;
  }

  if (paused) {
    window.fbq("consent", "grant");
    paused = false;
  }
  return true;
}

/** Deja el Píxel cargado pero sin enviar nada (`consent revoke`). */
function pausePixel(): void {
  if (!window.fbq || !initializedPixelId || paused) return;
  // Si fbevents.js aún no ha cargado (red lenta), lo que haya en la cola del
  // snippet saldría al cargar aunque el visitante ya haya revocado, y con la
  // URL de ese momento, que puede ser de /platform (QA de LDT-692, ronda 2).
  // Se quitan las llamadas de medición; `init` y `consent` se quedan para que
  // fbevents.js sepa que está revocado.
  const queue = window.fbq.queue;
  if (!window.fbq.callMethod && Array.isArray(queue)) {
    for (let i = queue.length - 1; i >= 0; i--) {
      const method = (queue[i] as ArrayLike<unknown>)[0];
      if (method === "track" || method === "trackCustom") queue.splice(i, 1);
    }
  }
  try {
    window.fbq("consent", "revoke");
  } catch {
    /* un fallo de Meta nunca rompe la página */
  }
  paused = true;
}

/** UUID para deduplicar el evento del navegador con el de la API de conversiones. */
export function newMetaEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Navegadores sin randomUUID (Safari < 15.4): un v4 con getRandomValues.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Manda un evento al Píxel y devuelve su `event_id`: el que se le pasa en
 * `opts.eventId` o uno nuevo. Lo devuelve SIEMPRE, se haya enviado o no, para
 * que quien llama pueda mandar el mismo id a su edge function (API de
 * conversiones) y Meta cuente la conversión una sola vez. No lanza nunca.
 */
export function trackMeta(event: MetaEventName, params?: MetaParams, opts?: { eventId?: string }): string {
  const eventId = opts?.eventId || newMetaEventId();
  if (!canSend(currentPathname())) return eventId;
  if (!ensurePixelLoaded()) return eventId;
  try {
    window.fbq!(STANDARD_SET.has(event) ? "track" : "trackCustom", event, params ?? {}, { eventID: eventId });
  } catch {
    /* un fallo de Meta nunca rompe la página */
  }
  return eventId;
}

/**
 * PageView de una ruta de la SPA (lo llama MetaPixelLoader en cada cambio de
 * `pathname`; el snippet oficial sólo lo manda en la primera carga). Fuera del
 * sitio público no manda nada y además pausa el Píxel si ya estaba cargado.
 */
export function trackMetaPageView(pathname: string): void {
  if (typeof window === "undefined") return;
  // Mismo pathname que el último visto (doble efecto de StrictMode, re-render
  // del loader): no se cuenta dos veces. Se anota ANTES de los gates para que
  // /pricing → /platform/login → /pricing vuelva a contar al volver.
  if (pathname === lastPageViewPath) return;
  lastPageViewPath = pathname;

  if (!canSend(pathname)) {
    pausePixel();
    return;
  }
  if (!ensurePixelLoaded()) return;
  try {
    window.fbq!("track", "PageView");
    const content = VIEW_CONTENT_PAGES[stripLocale(normalizePath(pathname))];
    if (content) window.fbq!("track", "ViewContent", { content_name: content }, { eventID: newMetaEventId() });
  } catch {
    /* un fallo de Meta nunca rompe la página */
  }
}

const META_COOKIES = ["_fbp", "_fbc"] as const;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// fbevents.js pone _fbp/_fbc en el dominio registrable (".playium.es") y, en
// hosts de sufijo público (*.vercel.app), en el host. Para borrarlas hay que
// repetir el mismo `domain`: se prueba sin dominio y con cada sufijo del host.
function deleteMetaCookies(): void {
  if (typeof document === "undefined") return;
  const labels = window.location.hostname.split(".");
  const domains: (string | null)[] = [null];
  for (let i = 0; i < labels.length - 1; i++) {
    const domain = labels.slice(i).join(".");
    domains.push(domain, `.${domain}`);
  }
  for (const name of META_COOKIES) {
    if (readCookie(name) === null) continue;
    for (const domain of domains) {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` + (domain ? `; domain=${domain}` : "");
    }
  }
}

/**
 * El visitante retira (o no ha dado) el consentimiento de marketing: el Píxel
 * deja de enviar y se borran sus cookies. Idempotente; seguro sin Píxel cargado.
 */
export function revokeMetaPixel(): void {
  if (typeof window === "undefined") return;
  pausePixel();
  // Si vuelve a aceptar en la misma página, cuenta como una vista nueva.
  lastPageViewPath = null;
  deleteMetaCookies();
}

// El fbclid sólo está en la URL de la página de aterrizaje: al navegar dentro
// de la SPA se pierde. Se recuerda en memoria (nada en disco ni en cookies:
// sin consentimiento no se escribe nada) con el momento en que se vio.
// Se evalúa al importar el módulo, también en el prerender: nunca puede lanzar.
const landingFbclid: { value: string; seenAt: number } | null = (() => {
  try {
    const value = new URLSearchParams(window.location.search).get("fbclid");
    return value ? { value, seenAt: Date.now() } : null;
  } catch {
    return null;
  }
})();

export interface MetaAttribution {
  fbp: string | null;
  fbc: string | null;
  consent_marketing: boolean;
  event_source_url: string | null;
}

/**
 * Datos de atribución que el navegador pasa a una edge function para que ésta
 * mande el evento por la API de conversiones (LDT-693 `/contact`, LDT-695
 * checkout). Sin consentimiento de marketing devuelve todo vacío: el servidor
 * tampoco puede mandar nada a Meta. `event_source_url` va sin query ni hash
 * (una query puede llevar cualquier cosa).
 */
export function getMetaAttribution(): MetaAttribution {
  if (typeof window === "undefined" || !marketingConsented()) {
    return { fbp: null, fbc: null, consent_marketing: false, event_source_url: null };
  }
  let fbc = readCookie("_fbc");
  if (!fbc) {
    // Formato de Meta para un fbc construido a mano: fb.1.<ms>.<fbclid>.
    const fromUrl = new URLSearchParams(window.location.search).get("fbclid");
    const clid = fromUrl ? { value: fromUrl, seenAt: Date.now() } : landingFbclid;
    if (clid) fbc = `fb.1.${clid.seenAt}.${clid.value}`;
  }
  return {
    fbp: readCookie("_fbp"),
    fbc,
    consent_marketing: true,
    event_source_url: `${window.location.origin}${window.location.pathname}`,
  };
}

/** Sólo para tests: vuelve al estado de una página recién cargada. */
export function _resetMetaPixelForTests(): void {
  initializedPixelId = null;
  paused = false;
  lastPageViewPath = null;
}
